import { Extension } from '@tiptap/core';
import { v4 } from 'uuid';
import { Plugin, PluginKey } from '@tiptap/pm/state';

const TableOfContentsPlugin = ({ getId, anchorTypes = ['heading'], }) => {
    return new Plugin({
        key: new PluginKey('tableOfContent'),
        appendTransaction(transactions, _oldState, newState) {
            const tr = newState.tr;
            let modified = false;
            if (transactions.some(transaction => transaction.docChanged)) {
                const existingIds = [];
                newState.doc.descendants((node, pos) => {
                    const nodeId = node.attrs['data-toc-id'];
                    if (!anchorTypes.includes(node.type.name) || node.textContent.length === 0) {
                        return;
                    }
                    if (nodeId === null || nodeId === undefined || existingIds.includes(nodeId)) {
                        let id = '';
                        if (getId) {
                            id = getId(node.textContent);
                        }
                        else {
                            id = v4();
                        }
                        tr.setNodeMarkup(pos, undefined, {
                            ...node.attrs,
                            'data-toc-id': id,
                            id,
                        });
                        modified = true;
                    }
                    existingIds.push(nodeId);
                });
            }
            return modified ? tr : null;
        },
    });
};

const getLastHeadingOnLevel = (headings, level) => {
    let heading = headings.filter(currentHeading => currentHeading.level === level).pop();
    if (level === 0) {
        return undefined;
    }
    if (!heading) {
        heading = getLastHeadingOnLevel(headings, level - 1);
    }
    return heading;
};
const getHeadlineLevel = (headline, previousItems) => {
    let level = 1;
    const previousHeadline = previousItems.at(-1);
    const highestHeadlineAbove = [...previousItems].reverse().find(h => h.originalLevel <= headline.node.attrs.level);
    const highestLevelAbove = (highestHeadlineAbove === null || highestHeadlineAbove === void 0 ? void 0 : highestHeadlineAbove.level) || 1;
    if (headline.node.attrs.level > ((previousHeadline === null || previousHeadline === void 0 ? void 0 : previousHeadline.originalLevel) || 1)) {
        level = ((previousHeadline === null || previousHeadline === void 0 ? void 0 : previousHeadline.level) || 1) + 1;
    }
    else if (headline.node.attrs.level < ((previousHeadline === null || previousHeadline === void 0 ? void 0 : previousHeadline.originalLevel) || 1)) {
        level = highestLevelAbove;
    }
    else {
        level = ((previousHeadline === null || previousHeadline === void 0 ? void 0 : previousHeadline.level) || 1);
    }
    return level;
};
const getLinearIndexes = (_headline, previousHeadlines) => {
    const previousHeadline = previousHeadlines.at(-1);
    if (!previousHeadline) {
        return 1;
    }
    return ((previousHeadline === null || previousHeadline === void 0 ? void 0 : previousHeadline.itemIndex) || 1) + 1;
};
const getHierarchicalIndexes = (headline, previousHeadlines, currentLevel) => {
    var _a, _b;
    const level = currentLevel || headline.node.attrs.level || 1;
    let itemIndex = 1;
    const previousHeadlinesOnLevelOrBelow = previousHeadlines.filter(h => h.level <= level);
    if (((_a = previousHeadlinesOnLevelOrBelow.at(-1)) === null || _a === void 0 ? void 0 : _a.level) === level) {
        itemIndex = (((_b = previousHeadlinesOnLevelOrBelow.at(-1)) === null || _b === void 0 ? void 0 : _b.itemIndex) || 1) + 1;
    }
    else {
        itemIndex = 1;
    }
    return itemIndex;
};
function debounce(func, wait) {
    let timeout = null;
    return (...args) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
}

const addTocActiveStatesAndGetItems = (content, options) => {
    const { editor } = options;
    const headlines = [];
    const scrolledOverIds = [];
    let activeId = null;
    if (editor.isDestroyed) {
        return content;
    }
    editor.state.doc.descendants((node, pos) => {
        var _a;
        const isValidType = (_a = options.anchorTypes) === null || _a === void 0 ? void 0 : _a.includes(node.type.name);
        if (!isValidType) {
            return;
        }
        headlines.push({ node, pos });
    });
    headlines.forEach(headline => {
        const domElement = editor.view.domAtPos(headline.pos + 1).node;
        const scrolledOver = options.storage.scrollPosition >= domElement.offsetTop;
        if (scrolledOver) {
            activeId = headline.node.attrs['data-toc-id'];
            scrolledOverIds.push(headline.node.attrs['data-toc-id']);
        }
    });
    content = content.map(heading => ({
        ...heading,
        isActive: heading.id === activeId,
        isScrolledOver: scrolledOverIds.includes(heading.id),
    }));
    if (options.onUpdate) {
        const isInitialCreation = options.storage.content.length === 0;
        options.onUpdate(content, isInitialCreation);
    }
    return content;
};
const setTocData = (options) => {
    const { editor, onUpdate } = options;
    if (editor.isDestroyed) {
        return;
    }
    const headlines = [];
    let anchors = [];
    const anchorEls = [];
    editor.state.doc.descendants((node, pos) => {
        var _a;
        const isValidType = (_a = options.anchorTypes) === null || _a === void 0 ? void 0 : _a.includes(node.type.name);
        if (!isValidType) {
            return;
        }
        headlines.push({ node, pos });
    });
    headlines.forEach((headline, i) => {
        if (headline.node.textContent.length === 0) {
            return;
        }
        const domElement = editor.view.domAtPos(headline.pos + 1).node;
        const scrolledOver = options.storage.scrollPosition >= domElement.offsetTop;
        anchorEls.push(domElement);
        const originalLevel = headline.node.attrs.level;
        const prevHeadline = headlines[i - 1];
        const level = options.getLevelFn(headline, anchors);
        const itemIndex = options.getIndexFn(headline, anchors, level);
        if (!prevHeadline) {
            anchors = [...anchors, {
                    itemIndex,
                    id: headline.node.attrs['data-toc-id'],
                    originalLevel,
                    level,
                    textContent: headline.node.textContent,
                    pos: headline.pos,
                    editor,
                    isActive: false,
                    isScrolledOver: scrolledOver,
                    node: headline.node,
                    dom: domElement,
                }];
            return;
        }
        anchors = [...anchors, {
                itemIndex,
                id: headline.node.attrs['data-toc-id'],
                originalLevel,
                level,
                textContent: headline.node.textContent,
                pos: headline.pos,
                editor,
                isActive: false,
                isScrolledOver: false,
                node: headline.node,
                dom: domElement,
            }];
    });
    anchors = addTocActiveStatesAndGetItems(anchors, options);
    if (onUpdate) {
        const isInitialCreation = options.storage.content.length === 0;
        onUpdate(anchors, isInitialCreation);
    }
    options.storage.anchors = anchorEls;
    options.storage.content = anchors;
    editor.state.tr.setMeta('toc', anchors);
    editor.view.dispatch(editor.state.tr);
};
const TableOfContents = Extension.create({
    name: 'tableOfContents',
    addStorage() {
        return {
            content: [],
            anchors: [],
            scrollHandler: () => null,
            scrollPosition: 0,
        };
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.anchorTypes || ['headline'],
                attributes: {
                    id: {
                        default: null,
                        renderHTML: attributes => {
                            return {
                                id: attributes.id,
                            };
                        },
                        parseHTML: element => {
                            return element.id || null;
                        },
                    },
                    'data-toc-id': {
                        default: null,
                        renderHTML: attributes => {
                            return {
                                'data-toc-id': attributes['data-toc-id'],
                            };
                        },
                        parseHTML: element => {
                            return element.dataset.tocId || null;
                        },
                    },
                },
            },
        ];
    },
    addOptions() {
        const defaultScrollParent = typeof window !== 'undefined' ? () => window : undefined;
        return {
            // eslint-disable-next-line
            onUpdate: () => { },
            // eslint-disable-next-line
            getId: (_textContent) => v4(),
            scrollParent: defaultScrollParent,
            anchorTypes: ['heading'],
        };
    },
    addCommands() {
        return {
            updateTableOfContents: () => ({ dispatch }) => {
                var _a;
                if (dispatch) {
                    setTocData({
                        editor: this.editor,
                        storage: this.storage,
                        onUpdate: (_a = this.options.onUpdate) === null || _a === void 0 ? void 0 : _a.bind(this),
                        getIndexFn: this.options.getIndex || getLinearIndexes,
                        getLevelFn: this.options.getLevel || getHeadlineLevel,
                        anchorTypes: this.options.anchorTypes,
                    });
                }
                return true;
            },
        };
    },
    onTransaction({ transaction }) {
        var _a;
        if (transaction.docChanged && !transaction.getMeta('toc')) {
            setTocData({
                editor: this.editor,
                storage: this.storage,
                onUpdate: (_a = this.options.onUpdate) === null || _a === void 0 ? void 0 : _a.bind(this),
                getIndexFn: this.options.getIndex || getLinearIndexes,
                getLevelFn: this.options.getLevel || getHeadlineLevel,
                anchorTypes: this.options.anchorTypes,
            });
        }
    },
    onCreate() {
        var _a;
        const { tr } = this.editor.state;
        const existingIds = [];
        if (this.options.scrollParent && typeof this.options.scrollParent !== 'function') {
            console.warn("[Tiptap Table of Contents Deprecation Notice]: The 'scrollParent' option must now be provided as a callback function that returns the 'scrollParent' element. The ability to pass this option directly will be deprecated in future releases.");
        }
        this.editor.state.doc.descendants((node, pos) => {
            var _a;
            const nodeId = node.attrs['data-toc-id'];
            const isValidType = (_a = this.options.anchorTypes) === null || _a === void 0 ? void 0 : _a.includes(node.type.name);
            if (!isValidType || node.textContent.length === 0) {
                return;
            }
            if (nodeId === null || nodeId === undefined || existingIds.includes(nodeId)) {
                let id = '';
                if (this.options.getId) {
                    id = this.options.getId(node.textContent);
                }
                else {
                    id = v4();
                }
                tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    'data-toc-id': id,
                    id,
                });
            }
            existingIds.push(nodeId);
        });
        this.editor.view.dispatch(tr);
        setTocData({
            editor: this.editor,
            storage: this.storage,
            onUpdate: (_a = this.options.onUpdate) === null || _a === void 0 ? void 0 : _a.bind(this),
            getIndexFn: this.options.getIndex || getLinearIndexes,
            getLevelFn: this.options.getLevel || getHeadlineLevel,
            anchorTypes: this.options.anchorTypes,
        });
        this.storage.scrollHandler = () => {
            var _a;
            if (!this.options.scrollParent) {
                return;
            }
            // if scrollParent is HTML Element, use scrollTop - otherwise use scrollY
            const scrollParent = typeof this.options.scrollParent === 'function' ? this.options.scrollParent() : this.options.scrollParent;
            const scrollPosition = scrollParent instanceof HTMLElement
                ? scrollParent.scrollTop
                : scrollParent.scrollY;
            this.storage.scrollPosition = scrollPosition || 0;
            const updatedItems = addTocActiveStatesAndGetItems(this.storage.content, {
                editor: this.editor,
                anchorTypes: this.options.anchorTypes,
                storage: this.storage,
                onUpdate: (_a = this.options.onUpdate) === null || _a === void 0 ? void 0 : _a.bind(this),
            });
            this.storage.content = updatedItems;
        };
        if (!this.options.scrollParent) {
            return;
        }
        const scrollParent = typeof this.options.scrollParent === 'function' ? this.options.scrollParent() : this.options.scrollParent;
        if (scrollParent) {
            scrollParent.addEventListener('scroll', this.storage.scrollHandler);
        }
    },
    onDestroy() {
        if (!this.options.scrollParent) {
            return;
        }
        const scrollParent = typeof this.options.scrollParent === 'function' ? this.options.scrollParent() : this.options.scrollParent;
        if (scrollParent) {
            scrollParent.removeEventListener('scroll', this.storage.scrollHandler);
        }
    },
    addProseMirrorPlugins() {
        return [
            TableOfContentsPlugin({ getId: this.options.getId, anchorTypes: this.options.anchorTypes }),
        ];
    },
});

export { TableOfContents, TableOfContentsPlugin, debounce, TableOfContents as default, getHeadlineLevel, getHierarchicalIndexes, getLastHeadingOnLevel, getLinearIndexes };
//# sourceMappingURL=index.js.map
